'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import SeverityBadge from '@/components/ui/SeverityBadge';

interface EventDetailDrawerProps {
  event: any;
  onClose: () => void;
}

export default function EventDetailDrawer({ event, onClose }: EventDetailDrawerProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const rawLog = `Mar 16 07:18:29 dmz-fw-01 kernel: [12847.932] IN=eth0 OUT=eth1 MAC=00:1a:2b:3c:4d:5e:ff:ff SRC=${event.srcIp} DST=${event.dstIp} LEN=60 TOS=0x00 PREC=0x00 TTL=51 ID=54321 DF PROTO=${event.protocol} SPT=${event.srcPort} DPT=${event.dstPort} WINDOW=29200 RES=0x00 SYN URGP=0 MARK=0x${Math.floor(Math.random()*65535).toString(16).toUpperCase()}`;

  const correlatedEvents = [
    { id: `EVT-${String(parseInt(event.id.split('-')[1]) - 1).padStart(6, '0')}`, type: 'Preceding auth attempt', ts: '07:17:44' },
    { id: `EVT-${String(parseInt(event.id.split('-')[1]) - 3).padStart(6, '0')}`, type: 'Same source IP — port scan', ts: '07:15:22' },
  ];

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-zinc-950 border-l border-zinc-800 z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-zinc-200">{event.id}</span>
              <SeverityBadge severity={event.severity} />
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">{event.eventType} — {event.timestamp}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin space-y-5">
          {/* Network Info */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Network Details</h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Source IP', value: event.srcIp, mono: true, color: 'text-amber-400' },
                { label: 'Source Port', value: String(event.srcPort), mono: true, color: 'text-zinc-300' },
                { label: 'Destination IP', value: event.dstIp, mono: true, color: 'text-blue-400' },
                { label: 'Destination Port', value: String(event.dstPort), mono: true, color: 'text-zinc-300' },
                { label: 'Protocol', value: event.protocol, mono: true, color: 'text-zinc-300' },
                { label: 'Bytes Transferred', value: event.bytes.toLocaleString(), mono: true, color: 'text-zinc-300' },
                { label: 'Country', value: event.country, mono: false, color: 'text-zinc-300' },
                { label: 'Asset', value: event.asset, mono: true, color: 'text-zinc-300' },
              ].map((field) => (
                <div key={field.label} className="bg-zinc-900 rounded-lg p-3 border border-zinc-800">
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wide mb-1">{field.label}</p>
                  <p className={`text-xs font-semibold ${field.mono ? 'font-mono' : ''} ${field.color}`}>{field.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Detection Info */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Detection Details</h4>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Rule Triggered</span>
                <span className="font-mono text-xs text-rose-400 font-semibold">{event.rule}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Event Type</span>
                <span className="text-xs text-zinc-300 font-medium">{event.eventType}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Severity</span>
                <SeverityBadge severity={event.severity} size="md" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Status</span>
                <span className="text-xs text-zinc-400 capitalize">{event.status}</span>
              </div>
            </div>
          </div>

          {/* Raw Log */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Raw Log</h4>
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 overflow-x-auto scrollbar-thin">
              <pre className="font-mono text-[11px] text-emerald-400/80 whitespace-pre-wrap break-all leading-relaxed">
                {rawLog}
              </pre>
            </div>
          </div>

          {/* Correlated Events */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Correlated Events ({correlatedEvents.length})</h4>
            <div className="space-y-2">
              {correlatedEvents.map((ce) => (
                <div key={ce.id} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 hover:border-zinc-700 transition-colors cursor-pointer">
                  <Icon name="LinkIcon" size={12} className="text-zinc-600 flex-shrink-0" />
                  <span className="font-mono text-xs text-zinc-400">{ce.id}</span>
                  <span className="text-xs text-zinc-500 flex-1">{ce.type}</span>
                  <span className="font-mono text-[11px] text-zinc-600">{ce.ts}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 p-4 border-t border-zinc-800 flex items-center gap-3">
          <button className="flex-1 px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95">
            Create Incident
          </button>
          <button className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium rounded-lg transition-colors">
            Acknowledge
          </button>
          <button className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium rounded-lg transition-colors">
            Suppress Rule
          </button>
        </div>
      </div>
    </>
  );
}